import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AtSign,
  Camera,
  CheckCircle2,
  CircleDot,
  Cloud,
  Copy,
  Download,
  Film,
  Flame,
  Heart,
  Link2,
  Music2,
  RefreshCw,
  Star,
  Wind,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const PLATFORM_CARDS = [
  { id: "onlyfans_vip", name: "OnlyFans VIP", icon: Heart, color: "text-sky-300", hub: "/crm/onlyfans-vip", extension: true },
  { id: "onlyfans_free", name: "OnlyFans Free", icon: Heart, color: "text-cyan-300", hub: "/crm/onlyfans-free", extension: true },
  { id: "justforfans", name: "JustForFans", icon: Flame, color: "text-orange-300", hub: "/platform-hubs/jff", extension: true },
  { id: "faphouse", name: "Faphouse", icon: Film, color: "text-amber-300", hub: "/platform-hubs/faphouse", extension: true },
  { id: "rawfuckclub", name: "RawFuckClub", icon: CircleDot, color: "text-rose-300", hub: "/crm/rfc", extension: true },
  { id: "theblowers", name: "TheBlowers", icon: Wind, color: "text-teal-300", hub: "/crm/theblowers", extension: true },
  { id: "top4fans", name: "Top4Fans", icon: Star, color: "text-yellow-300" },
  { id: "twitter", name: "X / Twitter", icon: AtSign, color: "text-zinc-200", hub: "/platform-hubs/x" },
  { id: "instagram", name: "Instagram", icon: Camera, color: "text-pink-300", hub: "/platform-hubs/instagram" },
  { id: "tiktok", name: "TikTok", icon: Music2, color: "text-cyan-200", hub: "/crm/tiktok" },
  { id: "bluesky", name: "Bluesky", icon: Cloud, color: "text-sky-300" },
  { id: "linktree", name: "Linktree", icon: Link2, color: "text-green-300" },
];

function latestTimestamp(values) {
  return values.filter(Boolean).sort((left, right) => new Date(right) - new Date(left))[0] || null;
}

function formatTimestamp(value) {
  if (!value) return "Never synced";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Never synced" : date.toLocaleString();
}

function connectionState(platform, syncRuns) {
  const accounts = platform?.accounts || [];
  const activeRun = syncRuns.find((run) => (
    run.platform === platform?.id
    && run.status === "running"
    && Date.now() - new Date(run.started_at).getTime() < 15 * 60 * 1000
  ));
  if (activeRun) return { label: "Syncing", style: "text-sky-300 border-sky-800 bg-sky-950/30" };
  if (platform?.state === "connected" || accounts.length) {
    return { label: "Connected", style: "text-emerald-300 border-emerald-800 bg-emerald-950/30" };
  }
  if (platform?.state === "extension_required" || platform?.state === "existing_flow") {
    return { label: "Extension required", style: "text-amber-300 border-amber-800 bg-amber-950/30" };
  }
  return { label: "Not connected", style: "text-zinc-300 border-zinc-700 bg-zinc-900" };
}

export default function PlatformConnections() {
  const [platforms, setPlatforms] = useState([]);
  const [syncRuns, setSyncRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [pairingCode, setPairingCode] = useState(null);
  const [sessionStates, setSessionStates] = useState([]);
  const [extensionRelease, setExtensionRelease] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [retryCycle, setRetryCycle] = useState(0);
  const refreshInFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (refreshInFlight.current) return false;
    refreshInFlight.current = true;
    try {
      const [platformResponse, syncResponse, sessionResponse, extensionResponse] = await Promise.all([
        api.get("/api/platform-data/platforms"),
        api.get("/api/platform-data/sync-runs"),
        api.get("/api/platform-data/connection-status"),
        api.get("/api/extension/version"),
      ]);
      setPlatforms(platformResponse.data?.platforms || []);
      setSyncRuns(syncResponse.data?.sync_runs || []);
      setSessionStates(sessionResponse.data?.platforms || []);
      setExtensionRelease(extensionResponse.data);
      setLoadError("");
      return true;
    } catch (error) {
      setLoadError(error?.response?.data?.detail || "Platform connection status is unavailable.");
      return false;
    } finally {
      setLoading(false);
      refreshInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    let active = true;
    let timer;
    let failures = 0;
    const poll = async () => {
      const available = await refresh();
      if (!active) return;
      failures = available ? 0 : failures + 1;
      if (failures >= 4) return;
      timer = window.setTimeout(poll, available ? 60000 : Math.min(60000, 1000 * (2 ** (failures - 1))));
    };
    poll();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [refresh, retryCycle]);

  const connect = async (platform) => {
    if (!platform.extension) return;
    setConnecting(true);
    try {
      const response = await api.post("/api/platform-data/pairing-codes", { platform: platform.pairingPlatform || platform.id });
      setPairingCode(response.data);
      toast.success("Secure pairing code created");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not create a pairing code");
    } finally {
      setConnecting(false);
    }
  };

  const syncNow = (account, platformName) => {
    if (account?.profile_url) window.open(account.profile_url, "_blank", "noopener,noreferrer");
    toast.info(`Open a supported ${platformName} page while signed in; the extension syncs it automatically. Manual refresh remains available in the extension popup.`);
  };

  const copyPairingCode = async () => {
    try {
      await navigator.clipboard.writeText(pairingCode.code);
      toast.success("Pairing code copied");
    } catch {
      toast.error("Could not copy the pairing code");
    }
  };

  const pairingPlatformName = PLATFORM_CARDS.find((platform) => platform.id === pairingCode?.platform)?.name || "platform";

  return (
    <div className="mx-auto max-w-7xl space-y-5" data-testid="platform-connections">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-zinc-800 pb-4">
        <div>
          <h2 className="text-lg font-semibold text-white">Platform connections</h2>
          <p className="mt-1 max-w-3xl text-sm text-zinc-400">
            Authentication, session handling, and syncing are managed by platform extensions. Cookies and credentials are never entered here.
          </p>
        </div>
        <button type="button" onClick={() => setRetryCycle((value) => value + 1)} disabled={loading} className="inline-flex min-h-9 items-center gap-2 rounded border border-zinc-700 px-3 text-sm text-zinc-200 hover:border-sky-500 disabled:opacity-50">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />Refresh status
        </button>
      </div>

      <p className="border-l-2 border-amber-500 px-3 py-2 text-sm text-zinc-400">
        The current Chrome extension automatically syncs paired, visible pages for OnlyFans VIP, OnlyFans Free, JustForFans, Faphouse, TheBlowers, and RawFuckClub. Manual refresh remains available in its popup.
      </p>

      {loadError && <p role="alert" className="border-l-2 border-amber-500 px-3 py-2 text-sm text-amber-300">Unavailable: {loadError}</p>}

      {pairingCode && (
        <section className="border border-orange-800 bg-orange-950/20 p-4" aria-live="polite">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-orange-200">{pairingPlatformName} pairing code</h3>
              <p className="mt-1 text-xs text-zinc-400">Enter this one-time code in the installed extension. Expires {formatTimestamp(pairingCode.expires_at)}.</p>
            </div>
            <button type="button" onClick={copyPairingCode} className="inline-flex items-center gap-2 rounded border border-orange-800 px-3 py-2 font-mono text-sm text-orange-100">
              <Copy size={14} />{pairingCode.code}
            </button>
          </div>
        </section>
      )}

      {loading && platforms.length === 0 ? (
        <p className="py-10 text-center text-sm text-zinc-500">Loading platform status…</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {PLATFORM_CARDS.map((definition) => {
            const platform = platforms.find((item) => item.id === definition.id);
            const accounts = platform?.accounts || [];
            const state = connectionState(platform, syncRuns);
            const session = sessionStates.find((item) => item.platform === (definition.pairingPlatform || definition.id));
            if (session) {
              state.label = ({ connected: "Connected", needs_reauth: "Reconnect via extension", extension_not_detected: "Extension not detected", disconnected: "Disconnected", unknown: "Session not verified" })[session.state] || "Session not verified";
              state.style = session.state === "connected" ? "text-emerald-300 border-emerald-800 bg-emerald-950/30" : "text-amber-300 border-amber-800 bg-amber-950/30";
            }
            const account = accounts[0];
            const lastSync = latestTimestamp([
              ...accounts.map((item) => item.last_synced_at),
              ...syncRuns.filter((run) => run.platform === definition.id).map((run) => run.completed_at),
            ]);
            const Icon = definition.icon;

            return (
              <section key={definition.id} className="flex min-w-0 flex-col border border-zinc-800 bg-zinc-950/70 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded border border-zinc-800 bg-zinc-900 ${definition.color}`}>
                      <Icon size={19} aria-hidden="true" />
                    </span>
                    <h3 className="truncate text-sm font-semibold text-zinc-100">{definition.name}</h3>
                  </div>
                  <span className={`shrink-0 rounded border px-2 py-1 text-[11px] ${state.style}`}>
                    {state.label === "Connected" && <CheckCircle2 size={12} className="mr-1 inline" />}
                    {state.label}
                  </span>
                </div>

                <p className="mt-3 min-h-5 truncate text-xs text-zinc-400">
                  {account ? `Connected account: ${account.username || account.account_id}` : "No account connected"}
                </p>
                <p className="mt-1 text-xs text-zinc-500">Last sync: {formatTimestamp(lastSync)}</p>
                {session && <p className="mt-1 text-xs text-zinc-500">Last session check: {formatTimestamp(session.last_verified_at)}</p>}

                <div className="mt-4 flex flex-wrap gap-2 border-t border-zinc-800 pt-3">
                  {definition.extension ? (
                    <>
                      {extensionRelease?.download_url && <a href={extensionRelease.download_url} download className="inline-flex items-center gap-1.5 rounded bg-zinc-800 px-2.5 py-2 text-xs text-zinc-100 hover:bg-zinc-700">
                        <Download size={13} />Download extension
                      </a>}
                      <button type="button" onClick={() => connect(definition)} disabled={connecting} className="rounded bg-orange-600 px-2.5 py-2 text-xs font-medium text-white hover:bg-orange-500 disabled:opacity-50">
                        {session?.state === "connected" ? "Reconnect via extension" : "Connect / Reconnect via extension"}
                      </button>
                      {account && <button type="button" onClick={() => syncNow(account, definition.name)} className="rounded border border-zinc-700 px-2.5 py-2 text-xs text-zinc-200 hover:border-sky-500">Open page to sync</button>}
                    </>
                  ) : (
                    <button type="button" disabled title="An extension adapter and download are not available yet" className="inline-flex cursor-not-allowed items-center gap-1.5 rounded border border-zinc-800 px-2.5 py-2 text-xs text-zinc-500">
                      <Download size={13} />Extension required
                    </button>
                  )}
                  {definition.hub && <a href={definition.hub} className="ml-auto inline-flex items-center gap-1 px-2 py-2 text-xs text-sky-300 hover:text-sky-200">Open hub <span aria-hidden="true">↗</span></a>}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
